import React, {FC, useMemo} from 'react';
import {LocationIcon, StarOIcon} from 'components';
import {statesAbbreviation} from 'definitions/States';
import {useRequesting} from 'hooks';
import {DirectBookingProviderType, SliceStatus} from 'interfaces';
import {useTranslation} from 'react-i18next';
import {Redirect} from 'react-router-dom';

import {useDirectBookingFilter} from '../useDirectBookingFilter';
import {mapProviderData} from '../utils';

import {TextLabel} from './DirectBookingLabel';
import DirectBookingProviderHeader from './DirectBookingProviderHeader';
import {DirectBookingProviderSkeleton} from './DirectBookingProviderSkeleton';

type Props = {
  provider: DirectBookingProviderType;
};

interface BioContentProps {
  bio: string;
}

const BioContent: React.FC<BioContentProps> = ({bio}) => {
  const parts = React.useMemo(() => bio.split(/(https?:\/\/[^\s]+)/g), [bio]);

  return (
    <>
      {parts.map((part, index) => {
        if (part.match(/^https?:\/\//)) {
          return (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline break-all"
            >
              {part}
            </a>
          );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
};

const DirectBookingProviderProfile: FC<Props> = ({provider}: Props) => {
  const {selectedOptions} = useDirectBookingFilter();
  const status = useRequesting('directBooking');

  const {
    acceptedInsurances,
    bio,
    conditions,
    education,
    ethnicity,
    languages,
    fullName,
    headshot,
    licences,
    states,
  } = mapProviderData(provider);

  const stateLabels = useMemo(
    () => statesAbbreviation.filter(s => states.includes(s.value)),
    [provider],
  );

  const isLoading = status === SliceStatus.pending;
  const isRejected = status === SliceStatus.rejected;
  const notFound =
    status === SliceStatus.resolved && !Object.keys(provider).length;

  const {t} = useTranslation();

  return (
    <section className="pb-16 text-left leading-[26px] mx-auto max-w-[600px]">
      {isLoading ? (
        <DirectBookingProviderSkeleton />
      ) : isRejected ? (
        <p>oops! something went wrong</p>
      ) : notFound ? (
        <Redirect to="/direct-booking/search-providers" />
      ) : (
        <>
          <DirectBookingProviderHeader
            headshot={headshot}
            name={fullName}
            licences={licences}
          />

          {bio ? (
            <>
              <h2 className="text-md py-4 font-semibold">
                {t('overview', 'Overview')}
              </h2>
              <p className="text-md mb-4">
                <BioContent bio={bio} />
              </p>
            </>
          ) : null}

          {education ? (
            <>
              <h2 className="text-md py-4 font-semibold">
                {t('education', 'Education')}
              </h2>
              <p className="font-sm mb-4">{education}</p>
            </>
          ) : null}

          {Array.isArray(acceptedInsurances) && acceptedInsurances?.length ? (
            <>
              <h2 className="text-md pt-4 pb-1 font-semibold">
                Insurances Accepted
              </h2>
              <section className="font-sm mb-4 flex items-center flex-wrap">
                {acceptedInsurances.map((s, idx) => (
                  <TextLabel text={s} key={+idx} />
                ))}
              </section>
            </>
          ) : null}

          {Array.isArray(conditions) && conditions.length > 0 ? (
            <>
              <h2 className="text-md pt-4 pb-1 font-semibold">
                {t('specialities', 'Specialities')}
              </h2>
              <div className="font-sm mb-4 flex items-center flex-wrap">
                {conditions.map(s => {
                  const selected = !!selectedOptions?.conditions?.find(
                    c => c === s,
                  );
                  return (
                    <TextLabel
                      text={s}
                      selected={selected}
                      key={s}
                      icon={
                        selected ? <StarOIcon height={13} width={13} /> : null
                      }
                    />
                  );
                })}
              </div>
            </>
          ) : null}

          {Array.isArray(stateLabels) && stateLabels.length ? (
            <>
              <h2 className="text-md pt-4 pb-1 font-semibold">Licensed in</h2>
              <div className="font-sm mb-4 flex items-center flex-wrap">
                {stateLabels.map(s => (
                  <TextLabel
                    text={s.label}
                    selected={selectedOptions?.state === s.value}
                    key={s.value}
                    icon={
                      selectedOptions?.state === s.value ? (
                        <LocationIcon height={15} width={15} />
                      ) : null
                    }
                  />
                ))}
              </div>
            </>
          ) : null}

          {Array.isArray(languages) && languages?.length ? (
            <>
              <h2 className="text-md pt-4 pb-1 font-semibold">
                {t('languages', 'Languages')}
              </h2>
              <div className="font-sm mb-4 flex items-center flex-wrap">
                {languages.map(s => {
                  const selected = !!selectedOptions?.languages?.find(
                    c => c === s,
                  );
                  return (
                    <TextLabel
                      text={s}
                      selected={selected}
                      key={s}
                      icon={
                        selected ? <StarOIcon height={13} width={13} /> : null
                      }
                    />
                  );
                })}
              </div>
            </>
          ) : null}

          {Array.isArray(ethnicity) && ethnicity.length ? (
            <>
              <h2 className="text-md pt-4 pb-1 font-semibold">
                {t('ethnicity', 'Ethnicity')}
              </h2>
              <div className="font-sm mb-4 flex items-center flex-wrap">
                {ethnicity.map(s => {
                  const selected = !!selectedOptions?.ethnicity?.find(
                    c => c === s,
                  );
                  return (
                    <TextLabel
                      text={s}
                      selected={selected}
                      key={s}
                      icon={
                        selected ? <StarOIcon height={13} width={13} /> : null
                      }
                    />
                  );
                })}
              </div>
            </>
          ) : null}
        </>
      )}
    </section>
  );
};

export default DirectBookingProviderProfile;
